import React, { useCallback, useMemo, useState } from "react"
import { GetStaticProps, GetStaticPropsContext } from "next"
import { useRouter } from "next/router"
import Image from "next/image"

import { JSONAPI, OMUniverse } from "@apptivity-lab/firmament-node-sdk"
import { restoreServerEnvironment, useAuthenticatedUser, useMachine, useProfile, useRestoredUniverse } from "@lib"

import { CatCategory } from "firmament-packages/categories"

import { AffAttendeeProfile, AffExpertProfile, AffLesson } from "@firmament-packages/aff"
import "../../firmament-packages/aff/extensions/AffAttendeeProfileExtensions"

import { AffLessonListStateMachine } from "@machines/AffLessonListStateMachine"

import AffLessonFilters from "@components/domain/AffLesson/AffLessonFilters"
import AffLessonCell from "@components/domain/AffLesson/AffLessonCell"

import AFFSidebarLayout from "@components/domain/AFFSidebarLayout"

const MARKETING_WRITE_UP = "Missed a class? Don't worry, you can access many of our past classes here in our On-Demand library. You can use the Filters to find a class you like, and press Start Now to take the class on your own time."

type PageProps = {
    cache: {
        categories: JSONAPI,
        expertProfiles: JSONAPI
    }
}

const MarketingHeader: React.FunctionComponent<{}> = ({}) => (
    <div className="relative">
        <div className="absolute w-full h-full">
            <Image layout="fill" objectFit="cover" src="/images/bg_marketing_banner.jpg" alt={MARKETING_WRITE_UP} />
        </div>

        <div className="flex py-[32px]">
            <div className="relative w-[90%] sm:w-[64%] max-w-[640px] mx-auto">
                <div className="border-[4px] border-black-900">
                    <div className="bg-white-900 m-[8px] p-[20px] sm:p-[40px]">
                        <h1 className="text-title1 text-black-900 text-center">On-Demand Classes</h1>
                        <h5 className="mt-[12px] text-subhead text-black-900 text-center">
                            {MARKETING_WRITE_UP}
                        </h5>

                        {/* TODO: Hiding Watch Tutorial for now */}
                        {/* <div className="mt-[16px] mx-auto text-center">
                            <span className="text-callout text-primary-900 uppercase underline">Watch Tutorial</span>
                        </div> */}
                    </div>
                </div>
            </div>
        </div>
    </div>
)

const LessonList: React.FunctionComponent<{
    lessons: AffLesson[]
}> = ({
    lessons
}) => {
    const router = useRouter()

    return (
        <div className="grid grid-cols-12 gap-[20px]">
            {lessons.map((lesson) => {
                return (
                    <AffLessonCell
                        key={lesson.id} variant="LARGE_GRID" lesson={lesson}
                        className="col-span-12 sm:col-span-6 xl:col-span-4 cursor-pointer"
                        onClick={(lesson) => router.push(`/on-demand/${lesson.id}`)}
                        />
                )
            })}
        </div>
    )
}

function AffVideoListPage({ cache }: PageProps) {
    useAuthenticatedUser("/auth/login")

    const attendeeProfile = useProfile(AffAttendeeProfile)

    const router = useRouter()

    const [machine, machineState] = useMachine(AffLessonListStateMachine)

    const [showingFullScreenFilter, setShowingFullScreenFilter] = useState<boolean>(false)

    const queryCategories = useMemo(() => {
        const { categories } = router.query
        if (typeof categories !== "string") {
            return []
        }

        const queryCategorySlugs = categories.split(",")

        const cachedCategories = OMUniverse.shared.apiClient.objectsFromResponse(CatCategory, cache.categories)
        return queryCategorySlugs.reduce((result: CatCategory[], slug: string) => {
            const category = cachedCategories.find((item) => item.slug === slug)
            if (category) {
                result.push(category)
            }
            return result
        }, [])
    }, [router.query, cache.categories])

    const [categoriesFilter, setCategoriesFilter] = useState<Array<CatCategory>>(queryCategories)
    const [expertsFilter, setExpertsFilter] = useState<Array<AffExpertProfile>>()

    const combinedFilters: Array<CatCategory | AffExpertProfile> = useMemo(() => {
        const combined: Array<CatCategory | AffExpertProfile> = []
        combined.push(...(categoriesFilter || []))
        combined.push(...(expertsFilter || []))
        return combined
    }, [categoriesFilter, expertsFilter])

    const removeFilterHandler = useCallback((filter: CatCategory | AffExpertProfile) => {
        if (filter instanceof CatCategory) {
            setCategoriesFilter((categoriesFilter || []).filter((category) => category.id !== filter.id))
        } else {
            setExpertsFilter((expertsFilter || []).filter((expert) => expert.id !== filter.id))
        }
    }, [categoriesFilter, expertsFilter])

    useRestoredUniverse(useMemo(() => () => {
        if (attendeeProfile && !attendeeProfile?.currentSubscription()) {
            router.replace("/profile/membership")
            return
        }

        machine.refresh({
            categories: (categoriesFilter || []).map((category) => category.slug || ""),
            providers: (expertsFilter || []).map((profile) => profile.id)
        })
    }, [attendeeProfile, router, machine, categoriesFilter, expertsFilter]))

    return (
        <div>
            {showingFullScreenFilter &&
                <div className="fixed md:hidden w-full h-[92vh] bg-white-900 z-30 overflow-auto overscroll-contain">
                    <AffLessonFilters
                        cancelable={true}
                        categories={OMUniverse.shared.apiClient.objectsFromResponse(CatCategory, cache.categories)}
                        experts={OMUniverse.shared.apiClient.objectsFromResponse(AffExpertProfile, cache.expertProfiles)}
                        selectedCategories={categoriesFilter}
                        selectedExperts={expertsFilter}
                        onSelectedCategoriesChange={(categories) => setCategoriesFilter(categories)}
                        onSelectedProfilesChange={(expertProfiles) => setExpertsFilter(expertProfiles)}
                        onDismiss={() => showingFullScreenFilter && setShowingFullScreenFilter(false)}
                        />
                </div>
            }

            <MarketingHeader />

            <div className="p-[20px] bg-white-900 shadow">
                <div className="flex">
                    <p className="flex-auto my-auto text-footnote sm:text-body text-black-700">Use the filters to find your perfect class</p>

                    <button type="button" className="flex md:hidden px-[16px] py-[12px] border-[1px] border-primary-900 hover:bg-primary-100 rounded-[8px] text-callout text-primary-900 uppercase"
                        onClick={() => setShowingFullScreenFilter(!showingFullScreenFilter)}>
                        <span>Filters</span>
                        <span className="ml-[8px] w-[24px] h-[24px]">
                            <Image width="24" height="24" src="/images/ic_filters_primary.svg" alt="Use the filters to find your perfect class" />
                        </span>
                    </button>
                </div>

                {combinedFilters.length > 0 &&
                    <>
                        <hr className="md:hidden border-gray-200 mt-[16px]" />

                        <div className="md:hidden flex flex-wrap mt-[16px] gap-[16px]">
                        {combinedFilters.map((each) => {
                            return (
                                <div key={each.id} className="flex chip-primary-selected">
                                    <span className="flex-auto">{each instanceof CatCategory ? each.title || "" : each.name || ""}</span>
                                    <button type="button" className="w-[24px] h-[24px] ml-[8px] my-auto"
                                        onClick={() => removeFilterHandler(each)}>
                                        <Image width={24} height={24} objectFit="contain"
                                            src="/images/ic_close_small_white.svg"
                                            alt="Remove Filter" />
                                    </button>
                                </div>
                            )
                        })}
                        </div>
                    </>
                }
            </div>

            <div className="grid grid-cols-12 bg-primary-100 min-h-[50vh] p-[20px] gap-[20px]">
                <div className="hidden md:block md:col-span-4 lg:col-span-3">
                    <div className="rounded-[8px] overflow-auto shadow">
                        <AffLessonFilters
                            categories={OMUniverse.shared.apiClient.objectsFromResponse(CatCategory, cache.categories)}
                            experts={OMUniverse.shared.apiClient.objectsFromResponse(AffExpertProfile, cache.expertProfiles)}
                            selectedCategories={categoriesFilter}
                            selectedExperts={expertsFilter}
                            onSelectedCategoriesChange={(categories) => setCategoriesFilter(categories)}
                            onSelectedProfilesChange={(expertProfiles) => {
                                setExpertsFilter(expertProfiles)
                            }}
                            />
                    </div>
                </div>

                <div className="col-span-12 md:col-span-8 lg:col-span-9">
                    <LessonList lessons={machineState.name === "start" ? [] : machineState.lessons} />
                </div>
            </div>
        </div>
    )
}

AffVideoListPage.Layout = AFFSidebarLayout

export const getStaticProps: GetStaticProps<
    PageProps,
    any,
    any
> = async (context: GetStaticPropsContext) => {
    try {
        await restoreServerEnvironment()

        const categoryQuery = CatCategory.query()
            .sort("title", "ASC")
            .limit(100)

        const expertProfileQuery = AffExpertProfile.query()
            .sort("name", "ASC")
            .limit(50)

        await Promise.all([
            categoryQuery.execute(),
            expertProfileQuery.execute()
        ])

        return {
            props: {
                cache: {
                    categories: categoryQuery.response.data,
                    expertProfiles: expertProfileQuery.response.data
                }
            },
            // Next.js will attempt to re-generate the page:
            // - When a request comes in
            // - At most once every 900 seconds
            revalidate: 900
        }
    } catch (error: unknown) {
        return {
            notFound: true
        }
    }
}

export default AffVideoListPage
